import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { PresenceParticipant } from '../endpoints/presence/list_GET.schema';

export interface MentionMatch {
  index: number;
  length: number;
  query: string;
  userId?: number;
  displayName?: string;
}

export interface ParsedMessage {
  type: 'direct' | 'ai' | 'broadcast';
  mentions: MentionMatch[];
  cleanedText: string;
  targetUserIds: number[];
}

/**
 * Hook for detecting and managing @mentions in text input
 */
export function useMentions(participants: PresenceParticipant[]) {
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStartIndex, setMentionStartIndex] = useState<number>(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // Filter participants based on mention query
  const mentionSuggestions = useMemo(() => {
    if (mentionQuery === null) return [];

    const query = mentionQuery.toLowerCase();
    return participants.filter((p) => p.displayName.toLowerCase().includes(query)).slice(0, 5); // Limit to 5 suggestions
  }, [mentionQuery, participants]);

  /**
   * Check if we're currently typing a mention
   */
  const detectMention = useCallback((text: string, cursorPosition: number) => {
    // Look backwards from cursor for @ symbol
    let atIndex = -1;
    for (let i = cursorPosition - 1; i >= 0; i--) {
      const char = text[i];
      if (char === '@') {
        atIndex = i;
        break;
      }
      // Stop if we hit a space or newline (not a valid mention)
      if (char === ' ' || char === '\n') {
        break;
      }
    }

    if (atIndex === -1) {
      setMentionQuery(null);
      setMentionStartIndex(-1);
      return;
    }

    // Extract the query after @
    const query = text.substring(atIndex + 1, cursorPosition);

    // Don't show suggestions if there's a space in the query
    if (query.includes(' ')) {
      setMentionQuery(null);
      setMentionStartIndex(-1);
      return;
    }

    setMentionQuery(query);
    setMentionStartIndex(atIndex);
    setSelectedMentionIndex(0);
  }, []);

  /**
   * Complete a mention with the selected participant
   */
  const completeMention = useCallback(
    (
      text: string,
      participant: PresenceParticipant,
      cursorPosition: number,
    ): { newText: string; newCursorPosition: number } => {
      if (mentionStartIndex === -1) {
        return { newText: text, newCursorPosition: cursorPosition };
      }

      // Replace @query with @DisplayName
      const before = text.substring(0, mentionStartIndex);
      const after = text.substring(cursorPosition);
      const mentionText = `@${participant.displayName} `;

      const newText = before + mentionText + after;
      const newCursorPosition = mentionStartIndex + mentionText.length;

      // Reset mention state
      setMentionQuery(null);
      setMentionStartIndex(-1);
      setSelectedMentionIndex(0);

      return { newText, newCursorPosition };
    },
    [mentionStartIndex],
  );

  /**
   * Navigate mention suggestions with keyboard
   */
  const handleMentionKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (mentionSuggestions.length === 0) return false;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((i) => (i < mentionSuggestions.length - 1 ? i + 1 : 0));
        return true;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((i) => (i > 0 ? i - 1 : mentionSuggestions.length - 1));
        return true;
      }

      if (e.key === 'Tab' || e.key === 'Enter') {
        if (mentionQuery !== null) {
          e.preventDefault();
          return true; // Signal that we want to complete the mention
        }
      }

      if (e.key === 'Escape') {
        setMentionQuery(null);
        setMentionStartIndex(-1);
        return true;
      }

      return false;
    },
    [mentionSuggestions.length, mentionQuery],
  );

  /**
   * Reset mention state
   */
  const resetMention = useCallback(() => {
    setMentionQuery(null);
    setMentionStartIndex(-1);
    setSelectedMentionIndex(0);
  }, []);

  return {
    mentionQuery,
    mentionStartIndex,
    mentionSuggestions,
    selectedMentionIndex,
    setSelectedMentionIndex,
    detectMention,
    completeMention,
    handleMentionKeyDown,
    resetMention,
    isShowingMentions: mentionQuery !== null && mentionSuggestions.length > 0,
  };
}

/**
 * Parse a message to extract mentions and determine message type
 */
export function parseMessage(text: string, participants: PresenceParticipant[]): ParsedMessage {
  const mentionRegex = /@(\w+(?:\s+\w+)?)/g;
  const mentions: MentionMatch[] = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    const mentionName = match[1];
    const participant = participants.find(
      (p) => p.displayName.toLowerCase() === mentionName.toLowerCase(),
    );

    mentions.push({
      index: match.index,
      length: match[0].length,
      query: mentionName,
      userId: participant?.userId,
      displayName: participant?.displayName,
    });
  }

  // Get unique target user IDs from valid mentions
  const targetUserIds = [
    ...new Set(mentions.filter((m) => m.userId !== undefined).map((m) => m.userId!)),
  ];

  // Determine message type
  let type: 'direct' | 'ai' | 'broadcast' = 'ai';

  if (targetUserIds.length > 0) {
    // If message starts with @mention, it's a direct message
    const trimmedText = text.trim();
    if (trimmedText.startsWith('@')) {
      type = 'direct';
    } else {
      // @mentions in the middle of text are still AI messages but will notify users
      type = 'ai';
    }
  }

  // Clean the text by removing @mentions for direct messages
  let cleanedText = text;
  if (type === 'direct') {
    // Remove leading @mentions
    cleanedText = text.replace(/^(@\w+(?:\s+\w+)?\s*)+/, '').trim();
  }

  return {
    type,
    mentions,
    cleanedText,
    targetUserIds,
  };
}

/**
 * Format a message with highlighted mentions
 */
export function formatMentions(
  text: string,
  participants: PresenceParticipant[],
): React.ReactNode[] {
  const mentionRegex = /@(\w+(?:\s+\w+)?)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const mentionName = match[1];
    const participant = participants.find(
      (p) => p.displayName.toLowerCase() === mentionName.toLowerCase(),
    );

    // Add the mention as a styled span
    parts.push(
      <span
        key={match.index}
        style={{
          color: participant?.color ?? 'var(--primary)',
          fontWeight: 600,
          backgroundColor: 'color-mix(in srgb, var(--primary) 15%, transparent)',
          padding: '0 4px',
          borderRadius: '4px',
        }}
      >
        @{participant?.displayName ?? mentionName}
      </span>,
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}
